import { Button, Radio } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IProcess } from '../../../../interfaces/process';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { GradingSummary } from '../../../../../../common/enums/gradingSummary';
import { ReportStatus } from '../../../../../../common/enums/reportStatus';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { UrlBuilder } from '../../../../../../common/utils/urlBuilder';
import { fetchJson, getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import {
    ClickableRowTable,
    FullName,
    Icon,
    InlineElement,
    InlineElementTextColor,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../elements/common';
import { ListPager } from '../../../../elements/ListPager';
import { Modal } from '../../../../elements/modals/Modal';
import { NullableFormattedDate } from '../../../../elements/NullableFormattedDate';
import { SelectRow } from '../../../../elements/tables/SelectRow';

interface IExistingExaminationModalProps {
    handleClose: (isSuccess?: true) => void;
    initialPotentialPreviousRequests: IProcess[];
    initialTotalPotentialPreviousRequests: number;
    isOpen: boolean;
    patientId: string;
    serviceId: string;
    serviceType: string;
    updateNewRequestPreviousRequest: (previousRequestId: string) => Promise<void>;
}

const ITEM_LIMIT_PER_PAGE = 3;

export function ExistingExaminationModal(props: IExistingExaminationModalProps) {
    const [selectedId, setSelectedId] = useState('');
    const [potentialPreviousRequests, setPotentialPreviousRequests] = useState<IProcess[]>(
        props.initialPotentialPreviousRequests || []
    );
    const [totalPotentialPreviousRequests, setTotalPotentialPreviousRequests] = useState(
        props.initialTotalPotentialPreviousRequests
    );
    const [isSearching, setIsSearching] = useState(false);
    const [page, setPage] = useState(1);

    useEffect(() => {
        setPotentialPreviousRequests(props.initialPotentialPreviousRequests);
    }, [props.initialPotentialPreviousRequests]);
    useEffect(() => {
        setTotalPotentialPreviousRequests(props.initialTotalPotentialPreviousRequests);
        if (props.initialTotalPotentialPreviousRequests === 1 && props.initialPotentialPreviousRequests.length) {
            setSelectedId(props.initialPotentialPreviousRequests[0].id);
        }
    }, [props.initialTotalPotentialPreviousRequests, props.initialPotentialPreviousRequests]);

    return (
        <Modal
            actionButtons={[
                <Button
                    onClick={() => props.handleClose()}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>,
                <Button
                    isDisabled={!selectedId}
                    onClick={onContinue}
                >
                    {translateKey('common.addTo')}
                </Button>,
            ]}
            isOpen={props.isOpen}
            onClose={() => props.handleClose()}
            size="large"
            title={
                props.serviceType === ServiceType.consultation
                    ? translateKey('common.useExistingExamination')
                    : translateKey('common.useExistingExaminationOrConsult')
            }
        >
            <ClickableRowTable>
                <TableHead>
                    <TableRow>
                        <TableHeader width={3}></TableHeader>
                        <TableHeader>{translateKey('common.type')}</TableHeader>
                        <TableHeader>{translateKey('common.conclusion')}</TableHeader>
                        <TableHeader>{translateKey('common.startedBy')}</TableHeader>
                        <TableHeader>{translateKey('request.lastUpdated')}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {potentialPreviousRequests.map((previousRequest) => (
                        <SelectRow
                            isSelected={previousRequest.id === selectedId}
                            key={`PreviousRequest_${previousRequest.id}`}
                            onClick={() => setSelectedId(previousRequest.id)}
                        >
                            <TableData align={'right'}>
                                <Radio
                                    checked={previousRequest.id === selectedId}
                                    readOnly
                                />
                            </TableData>
                            <TableData>
                                <Icon icon={getIcon()} />
                                <InlineElement>
                                    {getServiceLabel(previousRequest.code, 'request', previousRequest.name)}
                                </InlineElement>
                            </TableData>
                            <TableData>{getConclusionText(previousRequest)}</TableData>
                            <TableData>
                                <FullName {...previousRequest.requesterPractitioner} />
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={previousRequest.updated_at} />
                            </TableData>
                        </SelectRow>
                    ))}
                </TableBody>
            </ClickableRowTable>
            {totalPotentialPreviousRequests > ITEM_LIMIT_PER_PAGE && (
                <ListPager
                    isDisabled={isSearching}
                    itemsPerPage={ITEM_LIMIT_PER_PAGE}
                    onChangePage={fetchPotentialPreviousRequests}
                    page={page}
                    total={totalPotentialPreviousRequests}
                />
            )}
        </Modal>
    );

    function getConclusionText(previousRequest: IProcess) {
        if (previousRequest.diagnosticReports.length === 1) {
            if (previousRequest.diagnosticReports[0].gradingSummary === GradingSummary.deviant) {
                return (
                    <InlineElementTextColor color="emergency">
                        {translateKey('diagnosis.deviant')}
                    </InlineElementTextColor>
                );
            } else {
                return translateKey('common.normal');
            }
        }
    }

    function getIcon() {
        return potentialPreviousRequests[0].episodeCategory || '';
    }

    async function fetchPotentialPreviousRequests(newPage: number) {
        setPage(newPage);
        setIsSearching(true);

        const urlBuilder = new UrlBuilder('/api/requestProcesses');
        const url = urlBuilder
            .add('patientId', props.patientId)
            .add('serviceIdForPotentialPreviousRequests', props.serviceId)
            .add('limit', ITEM_LIMIT_PER_PAGE)
            .add('offset', (newPage - 1) * ITEM_LIMIT_PER_PAGE)
            .add('orderByCreatedAtDesc', true)
            .build();

        const result = await getJson(url);
        setPotentialPreviousRequests(result.json.items);
        setTotalPotentialPreviousRequests(result.json.count);
        setIsSearching(false);
    }

    async function onContinue() {
        const selectedPreviousRequest = potentialPreviousRequests.find(
            (previousRequest) => previousRequest.id === selectedId
        );
        if (!selectedPreviousRequest) {
            return;
        }

        if (
            selectedPreviousRequest.diagnosticReports.length === 1 &&
            selectedPreviousRequest.diagnosticReports[0].reviewStatus === ReportStatus.open
        ) {
            const url = `/api/diagnosticReports/${selectedPreviousRequest.diagnosticReports[0].id}/close?noAutoSubsequentServices=true`;
            await fetchJson(url, 'POST');
        }

        props.handleClose(true);
        await props.updateNewRequestPreviousRequest(selectedId);
    }
}
